import React, { useState, useEffect } from 'react';
import { Database, CheckCircle, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export const DatabaseStatus: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const [migrationStatus, setMigrationStatus] = useState<'pending' | 'running' | 'complete' | 'error'>('pending');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      setConnectionStatus('checking');
      
      // Test basic connection
      const { data, error } = await supabase.from('users').select('count').limit(1);
      
      if (error) {
        if (error.message.includes('relation "users" does not exist')) {
          setConnectionStatus('connected');
          setMigrationStatus('pending');
          setError('Database connected but migrations not applied yet.');
        } else {
          setConnectionStatus('error');
          setError(error.message);
        }
      } else {
        setConnectionStatus('connected');
        setMigrationStatus('complete');
      }
    } catch (err: any) {
      setConnectionStatus('error');
      setError(err.message || 'Failed to connect to database');
    }
  };

  const runMigrations = async () => {
    setMigrationStatus('running');
    
    try {
      // Check if tables exist
      const { error: usersError } = await supabase.from('users').select('count').limit(1);
      
      if (usersError && usersError.message.includes('relation "users" does not exist')) {
        setError('Please run the SQL migrations in your Supabase SQL editor first.');
        setMigrationStatus('error');
        return;
      }
      
      setMigrationStatus('complete');
    } catch (err: any) {
      setMigrationStatus('error');
      setError(err.message);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'checking':
      case 'running':
        return <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />;
      case 'connected':
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
      case 'complete':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'checking':
      case 'running':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-yellow-50 border-yellow-200';
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Database Status</h1>
        <p className="text-gray-600 mt-2">Check Supabase connection and migration status</p>
      </div>

      <div className="max-w-4xl space-y-6">
        <div className={`border rounded-lg p-6 ${getStatusColor(connectionStatus)}`}>
          <div className="flex items-center space-x-3 mb-4">
            {getStatusIcon(connectionStatus)}
            <h3 className="text-lg font-semibold text-gray-900">Database Connection</h3>
          </div>
          
          <div className="space-y-2">
            <p className="text-gray-700">
              Status: <span className="font-medium capitalize">{connectionStatus}</span>
            </p>
            {connectionStatus === 'connected' && (
              <p className="text-green-700">✅ Successfully connected to Supabase</p>
            )}
            {connectionStatus === 'error' && (
              <p className="text-red-700">❌ Connection failed: {error}</p>
            )}
          </div>
        </div>

        <div className={`border rounded-lg p-6 ${getStatusColor(migrationStatus)}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              {getStatusIcon(migrationStatus)}
              <h3 className="text-lg font-semibold text-gray-900">Database Migrations</h3>
            </div>
            
            {migrationStatus === 'pending' && (
              <button
                onClick={runMigrations}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Check Migrations
              </button>
            )}
          </div>
          
          <div className="space-y-2">
            <p className="text-gray-700">
              Status: <span className="font-medium capitalize">{migrationStatus}</span>
            </p>
            {migrationStatus === 'complete' && (
              <p className="text-green-700">✅ All database tables created successfully</p>
            )}
            {migrationStatus === 'error' && (
              <p className="text-red-700">❌ Migration error: {error}</p>
            )}
            {migrationStatus === 'pending' && (
              <p className="text-yellow-700">⏳ Migrations need to be applied</p>
            )}
          </div>
        </div>

        {migrationStatus === 'pending' || migrationStatus === 'error' ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">Setup Instructions</h3>
            <div className="space-y-4 text-blue-800">
              <div>
                <h4 className="font-medium">1. Connect to Supabase</h4>
                <p className="text-sm">Click the "Connect to Supabase" button in the top right corner</p>
              </div>
              
              <div>
                <h4 className="font-medium">2. Run Database Migrations</h4>
                <p className="text-sm">Go to your Supabase dashboard → SQL Editor and run the migration files:</p>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li><code>supabase/migrations/create_attendai_schema.sql</code></li>
                  <li><code>supabase/migrations/seed_demo_data.sql</code></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium">3. Verify Setup</h4>
                <p className="text-sm">Return here and click "Check Migrations" to verify everything is working</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-green-900 mb-2">🎉 Setup Complete!</h3>
            <p className="text-green-800">
              Your AttendAI database schema is set up! To complete the setup, you need to create user accounts.
            </p>
            
            <div className="mt-4 space-y-2">
              <h4 className="font-medium text-green-900">Next Step - Create Auth Users:</h4>
              <div className="bg-white p-4 rounded border">
                <p className="text-sm text-gray-700 mb-2">Go to your Supabase Dashboard → Authentication → Users and create:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>admin@attendai.com</strong> (password: demo123)</li>
                  <li>• <strong>data@attendai.com</strong> (password: demo123)</li>
                  <li>• <strong>integration@attendai.com</strong> (password: demo123)</li>
                </ul>
                <p className="text-sm text-gray-500 mt-2">
                  The user profiles will be automatically created in the database when they first log in.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-center">
          <button
            onClick={checkConnection}
            className="flex items-center space-x-2 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh Status</span>
          </button>
        </div>
      </div>
    </div>
  );
};